import { z } from "zod";

import { MediaAttachButton } from "./MediaAttachButton";
import {
  isAllowedMediaMimeType,
  MAX_MEDIA_ATTACHMENTS,
  MAX_MEDIA_FILE_SIZE_BYTES,
  MMS_BODY_CHARACTER_LIMIT,
  mediaBodyType,
  mediaSchema,
} from "../../channel/mmsConstraints";
import type { TextComposerExtension } from "../../editor/extensionContract";

type MediaItem = z.infer<typeof mediaSchema>;
type MediaSlice = MediaItem[];

declare module "../../editor/authoringState" {
  interface TextComposerAuthoringState {
    media?: MediaSlice;
  }
}

const MEDIA_OWNED_KEY = "media";
const mediaSliceSchema = z.array(mediaSchema);

function getExistingBodyText(body: {
  type: string;
  text?: string;
}): string | undefined {
  if (body.type === "TEXT") {
    return body.text;
  }
  if ("text" in body && typeof body.text === "string") {
    return body.text;
  }
  return undefined;
}

export const mediaExtension: TextComposerExtension<MediaSlice> = {
  id: "media",

  stateSchema: mediaSliceSchema,

  isEmpty: (value) => !value?.length,

  persistence: {
    ownedKeys: [MEDIA_OWNED_KEY],
    serialize: (slice) => ({ [MEDIA_OWNED_KEY]: slice }),
    deserialize: (raw) => {
      const candidate = raw[MEDIA_OWNED_KEY];
      if (candidate === undefined) return undefined;
      const parsed = mediaSliceSchema.safeParse(candidate);
      return parsed.success && parsed.data.length > 0 ? parsed.data : undefined;
    },
  },

  editor: {
    toolbarItems: [MediaAttachButton],
  },

  createActions: (set, get) => ({
    attachMedia(media: MediaItem) {
      set((state) => {
        state.authoringState.media = [media];
      });
    },
    removeMedia(index: number) {
      set((state) => {
        const currentMedia = get().authoringState.media ?? [];
        const nextMedia = currentMedia.filter(
          (_, mediaIndex) => mediaIndex !== index,
        );
        if (nextMedia.length === 0) {
          delete state.authoringState.media;
        } else {
          state.authoringState.media = nextMedia;
        }
      });
    },
  }),

  contentContribution: (state) => {
    if (!state.media?.length) return null;
    return { media: true };
  },

  transformRcs: (state, payload) => {
    const firstMedia = state.media?.[0];
    if (!firstMedia) return payload;

    const existingText = getExistingBodyText(payload.content.body);
    const mediaUrl = firstMedia.cdnUrl;

    return {
      content: {
        ...payload.content,
        body: {
          type: mediaBodyType(firstMedia.mimeType),
          url: mediaUrl,
          ...(existingText && existingText.length > 0
            ? { text: existingText }
            : {}),
        },
      },
    };
  },

  transformMms: (state, payload) => {
    const firstMedia = state.media?.[0];
    if (!firstMedia) return payload;

    const existingText = getExistingBodyText(payload.content.body);
    const mediaUrl = firstMedia.cdnUrl;

    return {
      content: {
        body: {
          type: mediaBodyType(firstMedia.mimeType),
          url: mediaUrl,
          ...(existingText && existingText.length > 0
            ? { text: existingText }
            : {}),
        },
      },
    };
  },

  validate: (state) => {
    if (!state.media?.length) return { valid: true };

    const errors: Record<string, string> = {};

    if (state.media.length > MAX_MEDIA_ATTACHMENTS) {
      errors.count = `Maximum ${MAX_MEDIA_ATTACHMENTS} attachment(s) allowed`;
    }

    if ((state.bodyText ?? "").length > MMS_BODY_CHARACTER_LIMIT) {
      errors.bodyLength = `Body text exceeds ${MMS_BODY_CHARACTER_LIMIT} character limit for media messages`;
    }

    for (const item of state.media) {
      if (!isAllowedMediaMimeType(item.mimeType)) {
        errors.mimeType = "Unsupported media type";
      }
      if (item.sizeBytes > MAX_MEDIA_FILE_SIZE_BYTES) {
        errors.size = "File is too large";
      }
    }

    return Object.keys(errors).length > 0
      ? { valid: false, errors }
      : { valid: true };
  },
};
